import { getApiBaseUrl } from "../config";

/**
 * Zoho Desk ASAP widget loader.
 *
 * Loads the widget once per page life and completes its one-time setup: JWT login and
 * ticket-form prefill. Memoised on the same pattern as utils/lazyVendors.js so concurrent
 * callers share one promise and the script is appended only once.
 */

let asapPromise;

const SCRIPT_ID = "zohodeskasapscript";

/**
 * Zoho's ready handshake, which MUST exist before the script tag is injected.
 *
 * This is the part of Zoho's inline snippet that is easy to miss when reproducing it in
 * JS, and omitting it breaks everything downstream. The sequence is:
 *
 *   1. the script we inject is only a small LOADER — it assigns window.ZohoDeskAsap and
 *      then injects the real SDK from static.zohocdn.com
 *   2. our script.onload therefore fires while the SDK is still in flight
 *   3. the SDK is what defines window.ZohoDeskAsapReady and sets ZohoDeskAsapReadyStatus
 *
 * So anything that waits for ZohoDeskAsapReady inside onload sees `undefined` and fails.
 * Defining this queueing stub up front lets callers register before the SDK lands; the SDK
 * later overwrites it and drains ZohoDeskAsap__asyncalls.
 *
 * Mirrors Zoho's own implementation exactly — do not "simplify" it.
 */
const installReadyStub = () => {
  if (typeof window.ZohoDeskAsapReady === "function") return;
  window.ZohoDeskAsapReady = function (callback) {
    const queue = (window.ZohoDeskAsap__asyncalls =
      window.ZohoDeskAsap__asyncalls || []);
    if (window.ZohoDeskAsapReadyStatus) {
      if (callback) queue.push(callback);
      queue.forEach((fn) => fn && fn());
      window.ZohoDeskAsap__asyncalls = null;
    } else if (callback) {
      queue.push(callback);
    }
  };
};

/**
 * Fetches a fresh Desk assertion.
 *
 * Called by Zoho on every login attempt, and deliberately never cached: the backend mints
 * a five-minute window, so a stored token is worthless within one sitting and actively
 * harmful across an email change.
 */
const fetchDeskToken = async () => {
  const response = await fetch(`${getApiBaseUrl()}/support/desk-token`, {
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error(`Desk token request failed with ${response.status}`);
  }
  const body = await response.json();
  const token = body?.data?.token;
  if (!token) throw new Error("Desk token response contained no token");
  return token;
};

/**
 * Each widget call is isolated. The ASAP API surface varies between SDK versions, and a
 * single unsupported call must not abort the rest of setup — that is precisely how a
 * failed `hide` previously took the prefill down with it.
 */
const safely = (label, fn) => {
  try {
    fn();
    return true;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(`[support] ZohoDeskAsap ${label} failed:`, error);
    return false;
  }
};

/**
 * Loads the widget, authenticates the member and applies the ticket-form prefill.
 * Resolves with window.ZohoDeskAsap.
 */
export const loadZohoAsap = (config = {}) => {
  const { scriptSrc, nonce, prefillKey, prefill } = config;
  if (!scriptSrc) return Promise.reject(new Error("Support widget is not configured"));

  if (!asapPromise) {
    asapPromise = new Promise((resolve, reject) => {
      if (typeof document === "undefined") {
        reject(new Error("Support widget requires a browser environment"));
        return;
      }

      // Must precede the script tag — see installReadyStub.
      installReadyStub();
      window.ZohoDeskAsapReady(() => resolve(window.ZohoDeskAsap));

      const script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.defer = true;
      script.src = scriptSrc;
      if (nonce) script.nonce = nonce;

      // A failed load must not poison the page: clear the memo so a later attempt retries
      // rather than replaying the same rejection forever.
      script.onerror = () => {
        asapPromise = undefined;
        script.remove();
        reject(new Error("Support widget failed to load"));
      };

      document.body.appendChild(script);
    }).then((asap) => {
      // Identify the member to Desk so their ticket history follows them. Auto-provisions
      // a Help Center end-user on first use — no Zoho account required.
      safely("login", () =>
        asap.invoke("login", async (successCallback, failureCallback) => {
          try {
            successCallback(await fetchDeskToken());
          } catch (error) {
            failureCallback(error);
          }
        }),
      );

      // Attaches the member's identity to the ticket for the agent. Both fields are hidden,
      // so the member neither sees nor edits them. The composite
      // "<departmentId>&&&<layoutId>" key is the shape Zoho documents, and is why both ids
      // are required config.
      if (prefillKey && prefill && Object.keys(prefill).length > 0) {
        safely("set prefillValues", () =>
          asap.set("ticket.form.prefillValues", { [prefillKey]: prefill }),
        );
      }

      // Pre-route to the ticket form, so the widget's own launcher lands where the portal
      // button used to send people — the same routeTo the button issued, minus the
      // invoke("open") that actually popped it up.
      //
      // This also forces the widget app to resolve its department and layout at load
      // rather than on first open, which is the step the launcher never performed on its
      // own. Deliberately no "open": nothing should appear unbidden.
      if (config.departmentId && config.layoutId) {
        safely("routeTo ticket.form (preload)", () =>
          asap.invoke("routeTo", {
            page: "ticket.form",
            parameters: {
              departmentId: config.departmentId,
              layoutId: config.layoutId,
            },
          }),
        );
      }

      return asap;
    });
  }

  return asapPromise;
};

/**
 * Opens the widget straight onto the ticket form. Used by explicit entry points; the
 * widget's own launcher does not need it.
 */
export const openZohoAsapTicketForm = async (config = {}) => {
  const asap = await loadZohoAsap(config);
  safely("routeTo ticket.form", () =>
    asap.invoke("routeTo", {
      page: "ticket.form",
      parameters: { departmentId: config.departmentId, layoutId: config.layoutId },
    }),
  );
  safely("open", () => asap.invoke("open"));
  return asap;
};

/**
 * Ends the Desk session. Called on portal logout — without it, the next person to use a
 * shared browser inherits the previous member's ticket history.
 *
 * Safe to call when the widget was never loaded, which is the common case.
 */
export const logoutZohoAsap = () => {
  try {
    window.ZohoDeskAsap?.invoke("logout");
  } catch {
    // Never block portal logout on the widget.
  }
};
